import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { MotivoPerda } from "@shared/schema";

const perdaFormSchema = z.object({
  quantidadeCachos: z.coerce.number().int().positive("Quantidade deve ser maior que zero"),
  motivoPerdaId: z.string().min(1, "Selecione um motivo"),
  observacoes: z.string().optional(),
  setorId: z.string().min(1, "Selecione um setor"),
});

type PerdaFormValues = z.infer<typeof perdaFormSchema>;

interface RegistrarPerdaDialogProps {
  cicloId: string;
  remanescente: number;
  setores: Array<{ id: string; nome: string }>;
}

export function RegistrarPerdaDialog({ cicloId, remanescente, setores }: RegistrarPerdaDialogProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const { data: motivosPerdas = [], isLoading: isLoadingMotivos } = useQuery<MotivoPerda[]>({
    queryKey: ["/api/motivos-perdas"],
  });

  const form = useForm<PerdaFormValues>({
    resolver: zodResolver(perdaFormSchema),
    defaultValues: {
      quantidadeCachos: 0,
      motivoPerdaId: "",
      observacoes: "",
      setorId: "",
    },
  });

  const createPerdaMutation = useMutation({
    mutationFn: async (data: PerdaFormValues) => {
      return await apiRequest("POST", "/api/perdas", {
        ...data,
        cicloId,
        usuarioId: JSON.parse(localStorage.getItem("user") || "{}").id,
      });
    },
    onSuccess: () => {
      toast({
        title: "Perda registrada",
        description: "A perda foi registrada com sucesso.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/ciclos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/perdas"] });
      form.reset();
      setOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao registrar perda",
        description: error.message || "Ocorreu um erro ao registrar a perda.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: PerdaFormValues) => {
    createPerdaMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm" data-testid="button-registrar-perda">
          <AlertTriangle className="h-4 w-4 mr-2" />
          Registrar Perda
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Registrar Perda</DialogTitle>
          <DialogDescription>
            Registre cachos perdidos para este ciclo. Remanescente: <strong>{remanescente} cachos</strong>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="setorId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Setor</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-setor">
                        <SelectValue placeholder="Selecione um setor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {setores.map((setor) => (
                        <SelectItem key={setor.id} value={setor.id}>
                          {setor.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="quantidadeCachos"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantidade de Cachos</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="0"
                      {...field}
                      data-testid="input-quantidade"
                    />
                  </FormControl>
                  <FormDescription>
                    Máximo: {remanescente} cachos
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="motivoPerdaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={isLoadingMotivos}>
                    <FormControl>
                      <SelectTrigger data-testid="select-motivo">
                        <SelectValue placeholder={isLoadingMotivos ? "Carregando..." : "Selecione um motivo"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {motivosPerdas.map((motivo) => (
                        <SelectItem key={motivo.id} value={motivo.id}>
                          {motivo.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="observacoes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva mais detalhes sobre a perda..."
                      className="resize-none"
                      {...field}
                      data-testid="textarea-observacoes"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={createPerdaMutation.isPending}
                data-testid="button-cancelar"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={createPerdaMutation.isPending}
                data-testid="button-confirmar"
              >
                {createPerdaMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Registrar Perda
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
